import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IChatSupportMessage, ISupportMessage } from "../../models/chatModels";
import { sortSupportMessages } from "./supportMessageSort";

const SUPPORT_CHAT_BAN_MESSAGE_CODE = "chat_ai_spam_ban_message";

const isSupportBanMessage = (message: Pick<ISupportMessage, "code" | "message">): boolean =>
    message.code === SUPPORT_CHAT_BAN_MESSAGE_CODE
    || message.message === SUPPORT_CHAT_BAN_MESSAGE_CODE;

const removeSupportBanMessages = (messages: IChatSupportMessage[]): IChatSupportMessage[] =>
    messages
        .map((group) => ({
            ...group,
            messages: group.messages.filter((message) => !isSupportBanMessage(message)),
        }))
        .filter((group) => group.messages.length > 0);

interface IChatSliceInitialState {
    chatMessages: IChatSupportMessage[];
    isActiveChat: boolean;
    chatId: number;
    msgNotAuth: ISupportMessage[];
    maxCountMessage: number;
    isChatBanned: boolean;
}

const chatSliceInitialState: IChatSliceInitialState = {
    chatMessages: [],
    isActiveChat: false,
    chatId: 0,
    msgNotAuth: [],
    maxCountMessage: 3,
    isChatBanned: false,
};

export const supportSlice = createSlice({
    name: "support",
    initialState: chatSliceInitialState,
    reducers: {
        resetSupportState: () => chatSliceInitialState,
        setSupportMessages: (state, action: PayloadAction<IChatSupportMessage[]>) => {
            state.chatMessages = action.payload;
        },
        setSupportId: (state, action: PayloadAction<number>) => {
            state.chatId = action.payload
        },
        addSupportMessages: (state, action: PayloadAction<IChatSupportMessage[]>) => {
            state.chatMessages = [...action.payload, ...state.chatMessages];
        },
        addSupportMessage: (state, action: PayloadAction<IChatSupportMessage>) => {
            const incomingMessages = action.payload.messages;
            const hasIncomingBanMessage = incomingMessages.some(isSupportBanMessage);

            if (hasIncomingBanMessage) {
                state.chatMessages = removeSupportBanMessages(state.chatMessages);
            }

            const incomingIds = incomingMessages.map((message) => message.id);

            if (
                !hasIncomingBanMessage
                && state.chatMessages.some((group) => group.messages.some((message) => incomingIds.includes(message.id)))
            ) {
                return;
            }

            if (
                state.chatMessages.find((el) => el.date === action.payload.date)
            ) {
                state.chatMessages.forEach((messagesElement) => {
                    if (messagesElement.date === action.payload.date) {
                        messagesElement.messages.push(
                            ...action.payload.messages
                        );
                        messagesElement.messages = sortSupportMessages(messagesElement.messages);
                    }
                });
            } else {
                state.chatMessages = [...state.chatMessages, action.payload];
            }
        },
        setSupportIsActiveChat: (state, action: PayloadAction<boolean>) => {
            state.isActiveChat = action.payload;
        },
        setSupportChatBanned: (state, action: PayloadAction<boolean>) => {
            state.isChatBanned = action.payload;
        },
        addSupportMessagesNotAuth: (state, action: PayloadAction<ISupportMessage[]>) => {
            state.msgNotAuth = action.payload;
        },
        addSupportMessageNotAuth: (state, action: PayloadAction<ISupportMessage>) => {
            state.msgNotAuth = sortSupportMessages([...state.msgNotAuth, action.payload]);
        },
        replaceSupportMessageNotAuth: (
            state,
            action: PayloadAction<{ tempId: number; message: ISupportMessage }>
        ) => {
            state.msgNotAuth = sortSupportMessages(
                state.msgNotAuth.map((item) =>
                    item.id === action.payload.tempId ? action.payload.message : item
                ),
            );
        },
        removeSupportMessageNotAuth: (state, action: PayloadAction<number>) => {
            state.msgNotAuth = state.msgNotAuth.filter((item) => item.id !== action.payload);
        },
        replaceSupportMessage: (
            state,
            action: PayloadAction<{ tempId: number; message: ISupportMessage }>
        ) => {
            const { tempId, message } = action.payload;

            state.chatMessages = state.chatMessages.map((group) => ({
                ...group,
                messages: group.messages.map((item) =>
                    item.id === tempId ? message : item
                ),
            }));

            // После replace optimistic id мог уже прийти по websocket — оставляем одно сообщение на id.
            const seenIds = new Set<number>();

            state.chatMessages = state.chatMessages
                .map((group) => ({
                    ...group,
                    messages: sortSupportMessages(
                        group.messages.filter((item) => {
                            if (seenIds.has(item.id)) {
                                return false;
                            }

                            seenIds.add(item.id);

                            return true;
                        }),
                    ),
                }))
                .filter((group) => group.messages.length > 0);
        },
        removeSupportMessage: (state, action: PayloadAction<number>) => {
            state.chatMessages = state.chatMessages
                .map((group) => ({
                    ...group,
                    messages: group.messages.filter((message) => message.id !== action.payload),
                }))
                .filter((group) => group.messages.length > 0);
        },
    },
});

export const supportReducer = supportSlice.reducer;
export const supportActions = supportSlice.actions;
